import type { UserPrivate } from '@repo/types/users';

import { useEditUser } from '@repo/api/user/useEditUser';
import { InfoIcon, User } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogBody, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { LoadingSwap } from '@/components/ui/loading-swap';

/**
 * Props for the ActivateUser component.
 * @property {UserPrivate | null} [user] - The user to activate/deactivate.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface ActivateUserProps {
  user?: UserPrivate | null;
  open: boolean;
  onOpenChange: (value: boolean) => void;
}

export default function ActivateUser(activateUserProps: ActivateUserProps) {
  const { user, open, onOpenChange } = activateUserProps;
  const { mutateAsync, isPending, isError, errorMessage } = useEditUser();

  const onSubmit = () => {
    if (user) {
      mutateAsync({ id: user.id, active: !user.active }).then(() => {
        onOpenChange(false);
      });
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className={'sm:max-w-96'}>
        <AlertDialogBody>
          <AlertDialogHeader>
            <FeaturedIcon>
              <User />
            </FeaturedIcon>
            <div className={'flex min-w-0 grow flex-col gap-0.5'}>
              <AlertDialogTitle>Zmienić aktywność użytkownika ?</AlertDialogTitle>
              <AlertDialogDescription className={'text-text-tertiary text-xs'}>Zmień akywność użytkownika, spowoduje jego zablokowanie/odblokowanie.</AlertDialogDescription>
            </div>
          </AlertDialogHeader>
          <div className={'flex flex-col p-4'}>
            <p className={'text-sx text-text-secondary'}>
              Czy na pewno chcesz zmienić aktywność użytkownika <b>{user?.name + ' ' + user?.surname} ?</b>
            </p>
            <Alert
              variant={'destructive'}
              data-visible={isError}
              className="overflow-hidden opacity-100 transition-[height,opacity] duration-400 data-[visible=false]:max-h-0 data-[visible=false]:p-0 data-[visible=false]:opacity-0 data-[visible=true]:h-fit data-[visible=true]:opacity-100"
            >
              <InfoIcon />
              <AlertTitle>{errorMessage.title}</AlertTitle>
              <AlertDescription>{errorMessage.detail}</AlertDescription>
            </Alert>
          </div>
        </AlertDialogBody>
        <AlertDialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
          <AlertDialogCancel variant={'tertiary'}>Anuluj</AlertDialogCancel>
          <Button disabled={isPending} onClick={onSubmit}>
            <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
              Potwierdzam
            </LoadingSwap>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
